import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, TWO_FACTOR_TYPES } from "../../consts/api";
import { http } from "../../services/HttpService";
import { unwrapOr } from "../../utilities/general";
import { authWorkspaceSchema, composeAuthWorkspace } from "../../schemas/auth";
import { trustedDeviceToken } from "../../utilities/localStorage";

const requestSchema = yup.object({
  email: yup.string().required(),
  password: yup.string().required(),
});

const responseSchema = yup.object({
  wpuId: yup.number().required(),

  need2Fa: yup.bool().required(),

  alreadyLoggedIn: yup.bool().required(),

  accessToken: yup.string().nullable(),

  twoFactorType: yup
    .string()
    .test({
      test: (value) =>
        Object.values(TWO_FACTOR_TYPES).includes(value) || value === null,
    })
    .nullable(),

  googleAuthCode: yup.string().nullable(),

  workspaces: yup.array().of(authWorkspaceSchema).required(),

  passwordExpireInDays: yup.number().nullable(),

  securityQuestion: yup.string().nullable(),

  otpSentTo: yup.string().nullable(),

  trustDays: yup.number().nullable(),

  isPasscodeSet: yup.boolean().required(),
});

const composeResponse = (res) => {
  const data = unwrapOr(() => res.data.data, {});
  return {
    wpuId: data.wpuId,
    need2Fa: data["2fa"],
    resendToken2Fa: data["2fa_resend_token"],
    alreadyLoggedIn: data.alreadyLoggedIn.web,
    accessToken: data["access-token"] ? data["access-token"].web : null,
    twoFactorType: data.type || null,
    googleAuthCode: data.googleAuthCode || null,
    workspaces: (data.workspaces || []).map(composeAuthWorkspace),
    passwordExpireInDays: data.change_password_days || null,
    securityQuestion: data.security_question || "",
    otpSentTo: data.otp_sent_to || null,
    trustDays: data["2fa_trust_device_days"] || null,
    isPasscodeSet: Boolean(data.is_passcode_set),
  };
};

export function useLoginMutation(options = {}) {
  return useMutation(async (payload) => {
    const dto = requestSchema.validateSync(payload, {
      strict: true,
    });
    const res = await http.post(HTTP_ENDPOINTS.postLogin(), {
      email: dto.email,
      password: dto.password,
      trusted_device_token: trustedDeviceToken.get().map((item) => item.token),
      "g-recaptcha-response": dto.recaptchaToken,
    });
    return responseSchema.validateSync(composeResponse(res), {
      strict: true,
    });
  }, options);
}
